import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSaveHealthQuestionnaireAnswersMutation } from 'src/common/api/apollo/additional';
import {
  buildSaveInputsFromAnswerDelta,
  collectFollowUpParentMap,
} from 'src/components/forms/healthQuestionnaire/buildSaveAnswerInputs.util';
import type { AnyQuestion, QuestionnaireAnswers } from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.types';

const DEBOUNCE_MS = 1000;

function answersWithoutConfirmation(
  a: QuestionnaireAnswers,
): Omit<QuestionnaireAnswers, 'confirmed'> {
  return {
    questions: a.questions,
    checkboxes: a.checkboxes,
    textInputs: a.textInputs,
    tables: a.tables,
    followUpFields: a.followUpFields,
  };
}

export interface UseDebouncedSaveHealthQuestionnaireAnswerParams {
  answers: QuestionnaireAnswers;
  offerId?: string;
  brandCode?: string;
  questions: AnyQuestion[];
}

export interface UseDebouncedSaveHealthQuestionnaireAnswerResult {
  /** True while a debounced save is scheduled or a `saveHealthQuestionnaireAnswers` request is in flight. */
  isAutoSaveBusy: boolean;
}

/**
 * After the user stops changing answers for {@link DEBOUNCE_MS}, sends one
 * `saveHealthQuestionnaireAnswers` mutation with the delta since the last successful save.
 * Skips the confirmation checkbox and does not run when `offerId` / `brandCode` are missing.
 */
export function useDebouncedSaveHealthQuestionnaireAnswer({
  answers,
  offerId,
  brandCode,
  questions,
}: UseDebouncedSaveHealthQuestionnaireAnswerParams): UseDebouncedSaveHealthQuestionnaireAnswerResult {
  const [saveHealthQuestionnaireAnswers] = useSaveHealthQuestionnaireAnswersMutation();
  const followUpParentMap = useMemo(() => collectFollowUpParentMap(questions), [questions]);

  const [isAutoSaveBusy, setIsAutoSaveBusy] = useState(false);
  const timerScheduledRef = useRef(false);
  const mutationsInFlightRef = useRef(0);

  const answersRef = useRef(answers);
  answersRef.current = answers;

  const lastSavedRef = useRef<QuestionnaireAnswers | null>(null);
  const isFirstRunRef = useRef(true);

  const updateBusy = useCallback(() => {
    setIsAutoSaveBusy(
      timerScheduledRef.current || mutationsInFlightRef.current > 0,
    );
  }, []);

  useEffect(() => {
    if (!offerId || !brandCode) {
      timerScheduledRef.current = false;
      mutationsInFlightRef.current = 0;
      setIsAutoSaveBusy(false);
      return undefined;
    }

    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      lastSavedRef.current = answers;
      timerScheduledRef.current = false;
      mutationsInFlightRef.current = 0;
      setIsAutoSaveBusy(false);
      return undefined;
    }

    const significantPrev = answersWithoutConfirmation(lastSavedRef.current ?? answers);
    const significantNext = answersWithoutConfirmation(answers);
    if (JSON.stringify(significantPrev) === JSON.stringify(significantNext)) {
      timerScheduledRef.current = false;
      updateBusy();
      return undefined;
    }

    timerScheduledRef.current = true;
    updateBusy();

    const timer = window.setTimeout(() => {
      timerScheduledRef.current = false;

      const { current } = answersRef;
      const baseline = lastSavedRef.current;
      if (!baseline) {
        lastSavedRef.current = current;
        updateBusy();
        return;
      }

      const answerItems = buildSaveInputsFromAnswerDelta(baseline, current, followUpParentMap);
      if (answerItems.length === 0) {
        lastSavedRef.current = current;
        updateBusy();
        return;
      }

      mutationsInFlightRef.current += 1;
      updateBusy();

      saveHealthQuestionnaireAnswers({
        variables: {
          id: offerId,
          input: { brandCode, answers: answerItems },
        },
      })
        .then(() => {
          lastSavedRef.current = current;
        })
        .catch(() => {
          /* keep lastSavedRef so the next edit retries the delta */
        })
        .finally(() => {
          mutationsInFlightRef.current -= 1;
          updateBusy();
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      timerScheduledRef.current = false;
      updateBusy();
    };
  }, [answers, offerId, brandCode, followUpParentMap, saveHealthQuestionnaireAnswers, updateBusy]);

  return { isAutoSaveBusy };
}
